/*
 * cart-upsell.js — "Complete your cart" free-shipping upsell.
 *
 * Picks products that would take the cart over the free-shipping threshold.
 * Candidates come from two places, in this order of preference:
 *
 *   1. The merchant's PRIORITY COLLECTIONS (Theme settings -> Complete Your
 *      Cart Upsell -> Which products). Curated, and always tried first.
 *   2. Shopify's OWN related-product recommendations for what is already in
 *      the cart (/recommendations/products.json?intent=related). Native,
 *      already tuned by the store's Search & Discovery settings, and live.
 *
 * Everything is driven off data-* attributes on the wrapper written by
 * snippets/cart-upsell.liquid, so the block works anywhere it is rendered and
 * this file needs no per-page config. Four contexts render it today:
 * cart-drawer, cart-page, atc-modal, and checkout-intercept.
 */
(function () {
  'use strict';

  var RECS_PER_ANCHOR = 10; // how many recommendations we ask for per cart product
  var MAX_ANCHORS = 3;      // how many cart products we ask about (keeps it to 3 requests)

  // ponytail: 50 is the ceiling on how deep we look into a priority collection.
  // products.json ships full body_html per product, so 250 would be ~megabytes
  // on the cart page. If a merchant needs deeper reach the upgrade path is a
  // curated (smaller) collection, not a bigger number here.
  var COLLECTION_DEPTH = 50;

  var recsCache = {};       // product_id -> Promise<products[]>, one fetch per product per page
  var collCache = {};       // collection handle -> Promise<products[]>
  var refreshScheduled = false;
  var cartPageStale = false;  // an add happened while the checkout modal was open

  var INTERCEPT_SESSION_KEY = 'hfb_cart_upsell_intercept_shown';

  // ponytail: same 4-line formatter as free-gift.js. Sharing it would mean a
  // module loader for two callers; copy is cheaper than the plumbing.
  function moneyFormat(cents) {
    var dollars = cents / 100;
    if (dollars % 1 === 0) return '$' + dollars.toFixed(0);
    return '$' + dollars.toFixed(2);
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function sizedImage(url, width) {
    if (!url) return '';
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function fetchCart() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .catch(function () { return null; });
  }

  function fetchRecommendations(productId) {
    if (recsCache[productId]) return recsCache[productId];
    var url = '/recommendations/products.json?product_id=' + encodeURIComponent(productId) +
      '&limit=' + RECS_PER_ANCHOR + '&intent=related';
    recsCache[productId] = fetch(url, { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (json) { return (json && json.products) || []; })
      .catch(function () { return []; });
    return recsCache[productId];
  }

  /*
   * The two product endpoints do NOT speak the same dialect, and getting this
   * wrong is silent: /recommendations/products.json returns `price` in CENTS as
   * an integer, while /collections/<handle>/products.json has no product-level
   * price at all and quotes `variants[].price` as a DOLLAR STRING ("54.60").
   * Read one as the other and a $54.60 top ends up compared against the gap as
   * 54 cents. Normalise collection products into the recommendations shape so
   * everything downstream — pickProducts, tileHtml — sees one format.
   */
  function fromCollectionJson(p) {
    var variants = (p.variants || []).map(function (v) {
      return {
        id: v.id,
        available: !!v.available,
        price: Math.round(parseFloat(v.price) * 100) || 0
      };
    });
    if (!variants.length) return null;

    var prices = variants.map(function (v) { return v.price; });
    var min = Math.min.apply(null, prices);
    var max = Math.max.apply(null, prices);

    return {
      id: p.id,
      title: p.title,
      url: '/products/' + p.handle,
      featured_image: (p.images && p.images[0] && p.images[0].src) || '',
      price: min,
      price_varies: min !== max,
      available: variants.some(function (v) { return v.available; }),
      variants: variants
    };
  }

  function fetchCollection(handle) {
    if (collCache[handle]) return collCache[handle];
    var url = '/collections/' + encodeURIComponent(handle) +
      '/products.json?limit=' + COLLECTION_DEPTH;
    collCache[handle] = fetch(url, { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (json) {
        return ((json && json.products) || [])
          .map(fromCollectionJson)
          .filter(Boolean);
      })
      .catch(function () { return []; });
    return collCache[handle];
  }

  // Ask about the priciest lines first: they are the strongest signal of what
  // the shopper is actually shopping for.
  function anchorProductIds(cart) {
    var seen = {};
    var ids = [];
    cart.items.slice()
      .sort(function (a, b) { return b.final_line_price - a.final_line_price; })
      .forEach(function (line) {
        if (!line.product_id || seen[line.product_id]) return;
        seen[line.product_id] = true;
        ids.push(line.product_id);
      });
    return ids.slice(0, MAX_ANCHORS);
  }

  function flatten(lists) {
    return lists.reduce(function (all, list) { return all.concat(list); }, []);
  }

  /*
   * Returns { priority, related }. `related` is skipped entirely when the
   * merchant has ticked "Only suggest from these collections" — that setting
   * is a promise about what a shopper will be shown, not a ranking hint.
   */
  function candidatePool(cart, collections, collectionsOnly) {
    var priority = collections.length
      ? Promise.all(collections.map(fetchCollection)).then(flatten)
      : Promise.resolve([]);

    var ids = anchorProductIds(cart);
    var related = (collectionsOnly && collections.length) || !ids.length
      ? Promise.resolve([])
      : Promise.all(ids.map(fetchRecommendations)).then(flatten);

    return Promise.all([priority, related]).then(function (pair) {
      return { priority: pair[0], related: pair[1] };
    });
  }

  /*
   * The cheapest thing that actually closes the gap wins — it is the smallest
   * ask that still earns the shipping, which is the whole point of the block.
   * Products that close the gap rank first. If they do not fill every available
   * recommendation slot, the closest remaining products fill the row.
   *
   * `product.price` is the product's MINIMUM variant price, so `price >= gap`
   * means every variant closes the gap — never a promise we can't keep.
   *
   * Priority collections rank FIRST, but only within the same tier: a related
   * product that closes the gap still beats a priority product that doesn't.
   * Showing a suggestion that leaves the shopper short is the one outcome
   * worse than showing nothing, and a collection preference must not buy it.
   */
  function splitByGap(list, gapCents) {
    var closers = list.filter(function (p) { return p.price >= gapCents; })
      .sort(function (a, b) { return a.price - b.price; });
    var nears = list.filter(function (p) { return p.price < gapCents; })
      .sort(function (a, b) {
        return Math.abs(a.price - gapCents) - Math.abs(b.price - gapCents);
      });
    return { closers: closers, nears: nears };
  }

  function pickProducts(pool, cart, gapCents, limit) {
    var inCart = {};
    cart.items.forEach(function (line) { inCart[line.product_id] = true; });

    var seen = {};
    function usable(list) {
      return list.filter(function (p) {
        if (!p || !p.available || !p.price || inCart[p.id] || seen[p.id]) return false;
        seen[p.id] = true;
        return true;
      });
    }

    // Dedupe priority first so a product in both pools keeps its priority rank.
    var priority = splitByGap(usable(pool.priority), gapCents);
    var related = splitByGap(usable(pool.related), gapCents);

    var closers = priority.closers.concat(related.closers);
    var nears = priority.nears.concat(related.nears);
    return closers.concat(nears).slice(0, limit);
  }

  /*
   * Quick-add is only offered when the product has exactly one variant. This
   * is an apparel store: silently adding "whatever variant came first" would
   * put the wrong SIZE in someone's cart. Anything with choices links to the
   * product page instead.
   */
  function tileHtml(product, opts) {
    var onlyVariant = (product.variants || []).length === 1 ? product.variants[0] : null;
    var canQuickAdd = !!(onlyVariant && onlyVariant.available);
    var image = sizedImage(product.featured_image, 240);
    var action = canQuickAdd
      ? '<button type="button" class="cart-upsell__btn" data-cart-upsell-add="' + onlyVariant.id + '">' +
          escapeHtml(opts.addLabel) + '</button>'
      : '<a class="cart-upsell__btn cart-upsell__btn--link" href="' + escapeHtml(product.url) + '">' +
          escapeHtml(opts.chooseLabel) + '</a>';

    return '' +
      '<div class="cart-upsell__item">' +
        '<a class="cart-upsell__media" href="' + escapeHtml(product.url) + '" tabindex="-1" aria-hidden="true">' +
          (image ? '<img src="' + escapeHtml(image) + '" alt="" loading="lazy" width="120" height="150">' : '') +
        '</a>' +
        '<a class="cart-upsell__name" href="' + escapeHtml(product.url) + '">' + escapeHtml(product.title) + '</a>' +
        '<span class="cart-upsell__price">' + escapeHtml(moneyFormat(product.price)) +
          (product.price_varies ? '+' : '') + '</span>' +
        action +
      '</div>';
  }

  function readOptions(wrapper) {
    var d = wrapper.dataset;
    return {
      context: d.context || 'cart-page',
      thresholdCents: parseInt(d.thresholdCents, 10) || 0,
      minSubtotalCents: parseInt(d.minSubtotalCents, 10) || 0,
      useOriginalTotal: d.useOriginalTotal === 'true',
      limit: parseInt(d.limit, 10) || 4,
      columns: parseInt(d.columns, 10) || 2,
      collections: (d.collections || '').split(',').filter(Boolean),
      collectionsOnly: d.collectionsOnly === 'true',
      titleTemplate: d.titleTemplate || 'You are {{ amount }} away from FREE shipping',
      subtitle: d.subtitle || '',
      addLabel: d.addLabel || 'Add',
      chooseLabel: d.chooseLabel || 'Choose size',
      errorLabel: d.errorLabel || 'Try again',
      successText: d.successText || ''
    };
  }

  function subtotalFor(cart, opts) {
    return opts.useOriginalTotal
      ? (cart.original_total_price || cart.total_price)
      : cart.total_price;
  }

  function gapFor(cart, opts) {
    return opts.thresholdCents - subtotalFor(cart, opts);
  }

  // A block is "wanted" when the cart is big enough for the merchant's minimum
  // AND there is still a gap to close. Both gates, everywhere it renders.
  function wanted(cart, opts) {
    if (!cart.item_count) return false;
    if (subtotalFor(cart, opts) < opts.minSubtotalCents) return false;
    return gapFor(cart, opts) > 0;
  }

  function overlayOf(wrapper) {
    return wrapper.closest ? wrapper.closest('[data-cart-upsell-intercept]') : null;
  }

  function scrollIndicatorOf(itemsEl) {
    var indicator = itemsEl.nextElementSibling;
    if (indicator && indicator.classList.contains('cart-upsell__scrollbar')) return indicator;

    indicator = document.createElement('div');
    indicator.className = 'cart-upsell__scrollbar';
    indicator.setAttribute('aria-hidden', 'true');
    indicator.hidden = true;

    var thumb = document.createElement('span');
    thumb.className = 'cart-upsell__scrollbar-thumb';
    indicator.appendChild(thumb);
    itemsEl.insertAdjacentElement('afterend', indicator);
    return indicator;
  }

  function updateScrollIndicator(itemsEl) {
    var indicator = scrollIndicatorOf(itemsEl);
    var mobile = window.matchMedia && window.matchMedia('(max-width: 767px)').matches;
    var scrollable = itemsEl.classList.contains('is-scrollable') &&
      itemsEl.scrollWidth > itemsEl.clientWidth + 1;
    indicator.hidden = !mobile || !scrollable;
    if (indicator.hidden) return;

    var thumb = indicator.firstElementChild;
    var thumbPercent = Math.max(24, itemsEl.clientWidth / itemsEl.scrollWidth * 100);
    var maxScroll = itemsEl.scrollWidth - itemsEl.clientWidth;
    var progress = maxScroll > 0 ? itemsEl.scrollLeft / maxScroll : 0;
    progress = Math.max(0, Math.min(1, progress));
    thumb.style.width = thumbPercent + '%';
    thumb.style.left = progress * (100 - thumbPercent) + '%';
  }

  function scheduleScrollIndicator(itemsEl) {
    if (itemsEl._cartUpsellIndicatorFrame) return;
    itemsEl._cartUpsellIndicatorFrame = requestAnimationFrame(function () {
      itemsEl._cartUpsellIndicatorFrame = null;
      updateScrollIndicator(itemsEl);
    });
  }

  function enableScrollIndicator(itemsEl) {
    if (itemsEl._cartUpsellIndicatorBound) return;
    itemsEl._cartUpsellIndicatorBound = true;

    itemsEl.addEventListener('scroll', function () {
      scheduleScrollIndicator(itemsEl);
    }, { passive: true });

    window.addEventListener('resize', function () {
      scheduleScrollIndicator(itemsEl);
    });
  }

  function enableNativeDrawerSwipe(wrapper, itemsEl) {
    if (wrapper.dataset.context !== 'cart-drawer' || itemsEl._cartUpsellDrawerSwipeBound) return;
    itemsEl._cartUpsellDrawerSwipeBound = true;

    var startX = 0;
    var startY = 0;

    itemsEl.addEventListener('touchstart', function (event) {
      if (!itemsEl.classList.contains('is-scrollable') || event.touches.length !== 1) return;
      startX = event.touches[0].clientX;
      startY = event.touches[0].clientY;
    }, { passive: true });

    itemsEl.addEventListener('touchmove', function (event) {
      if (!itemsEl.classList.contains('is-scrollable') || event.touches.length !== 1) return;
      var deltaX = Math.abs(event.touches[0].clientX - startX);
      var deltaY = Math.abs(event.touches[0].clientY - startY);
      if (deltaX > deltaY) event.stopPropagation();
    }, { passive: true });
  }

  function closeIntercept(overlay, opts) {
    if (!overlay) return;
    overlay.hidden = true;
    document.documentElement.classList.remove('cart-upsell-intercept-open');
    // An add made inside the modal never reached the cart page's own line-item
    // table. Now that the modal is out of the way, show them the real cart —
    // unless they are on their way to checkout and will not see it anyway.
    if (cartPageStale && !(opts && opts.leaving) && /^\/cart(\/|$)/.test(window.location.pathname)) {
      cartPageStale = false;
      window.location.reload();
    }
  }

  function render(wrapper, cart, pool) {
    var opts = readOptions(wrapper);
    var overlay = overlayOf(wrapper);
    var open = overlay && !overlay.hidden;

    if (!wanted(cart, opts)) {
      // The one case where vanishing is wrong: the shopper closed the gap from
      // INSIDE the open intercept, so the modal would blank out mid-interaction.
      // Congratulate them and leave the checkout button under their cursor.
      if (open && cart.item_count && gapFor(cart, opts) <= 0 && opts.successText) {
        var okTitle = wrapper.querySelector('[data-cart-upsell-title]');
        var okSub = wrapper.querySelector('[data-cart-upsell-subtitle]');
        var okItems = wrapper.querySelector('[data-cart-upsell-items]');
        if (okTitle) okTitle.textContent = opts.successText;
        if (okSub) okSub.textContent = '';
        if (okItems) okItems.innerHTML = '';
        overlay.classList.add('is-complete');
        wrapper.hidden = false;
        return;
      }
      wrapper.hidden = true;
      if (open) closeIntercept(overlay);
      return;
    }

    var gap = gapFor(cart, opts);
    var picks = pickProducts(pool, cart, gap, opts.limit);
    if (!picks.length) {
      wrapper.hidden = true;
      if (open) closeIntercept(overlay);
      return;
    }

    var amount = moneyFormat(gap);
    var title = opts.titleTemplate
      .replace('{{ amount }}', amount)
      .replace('{{amount}}', amount);

    var titleEl = wrapper.querySelector('[data-cart-upsell-title]');
    if (titleEl) titleEl.textContent = title;

    var subtitleEl = wrapper.querySelector('[data-cart-upsell-subtitle]');
    if (subtitleEl) subtitleEl.textContent = opts.subtitle;

    var itemsEl = wrapper.querySelector('[data-cart-upsell-items]');
    if (itemsEl) {
      var visibleColumns = opts.context === 'cart-drawer' ? 2 : opts.columns;
      itemsEl.classList.toggle('is-scrollable', picks.length > visibleColumns);
      itemsEl.innerHTML = picks.map(function (p) { return tileHtml(p, opts); }).join('');
      itemsEl.scrollLeft = 0;
      enableScrollIndicator(itemsEl);
      enableNativeDrawerSwipe(wrapper, itemsEl);
      scheduleScrollIndicator(itemsEl);
    }
    if (overlay) overlay.classList.remove('is-complete');
    wrapper.hidden = false;
  }

  function refresh() {
    var wrappers = document.querySelectorAll('[data-cart-upsell]');
    if (!wrappers.length) return;

    fetchCart().then(function (cart) {
      if (!cart) return;

      // If no block wants to show, hide them all and skip the product requests
      // entirely — except an open intercept, which render() still has to close
      // or convert into its "free shipping earned" state.
      var live = Array.prototype.filter.call(wrappers, function (w) {
        if (wanted(cart, readOptions(w))) return true;
        var o = overlayOf(w);
        return !!(o && !o.hidden);
      });
      if (!live.length) {
        Array.prototype.forEach.call(wrappers, function (w) { w.hidden = true; });
        return;
      }

      // Every wrapper reads the same theme settings, so one pool serves all.
      var opts = readOptions(wrappers[0]);
      candidatePool(cart, opts.collections, opts.collectionsOnly).then(function (pool) {
        Array.prototype.forEach.call(wrappers, function (w) { render(w, cart, pool); });
      });
    });
  }

  function scheduleRefresh() {
    if (refreshScheduled) return;
    refreshScheduled = true;
    setTimeout(function () {
      refreshScheduled = false;
      refresh();
    }, 120);
  }

  function quickAdd(variantId, btn, opts) {
    btn.disabled = true;
    btn.classList.add('is-loading');

    fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    })
      .then(function (r) {
        return r.json().then(function (json) {
          if (!r.ok) throw new Error((json && json.description) || 'add failed');
          return json;
        });
      })
      .then(function (json) {
        // Hand the new line to the theme's own drawer constructor, then let
        // every cart-aware widget re-render — the same path free-gift.js uses.
        var line = json && json.items ? json.items[0] : json;
        try {
          if (line && window.addToCartHandler &&
              typeof window.addToCartHandler.addItem === 'function') {
            window.addToCartHandler.addItem(line);
          }
        } catch (e) { /* defensive — never block the user flow */ }
        return fetchCart();
      })
      .then(function (cart) {
        try {
          if (cart && window.addToCartHandler &&
              typeof window.addToCartHandler.updateGeneralInfo === 'function') {
            window.addToCartHandler.updateGeneralInfo(cart);
          }
        } catch (e) {}
        try { document.dispatchEvent(new CustomEvent('cart:updated')); } catch (e) {}

        /*
         * On /cart the line items live in the page body, not the drawer, so the
         * hook above cannot redraw them and the page has to reload to show the
         * new row. Rebuilding that table in place is NOT a safe substitute: the
         * theme's quantity inputs are bound directly at DOMContentLoaded
         * (sections/cart-template.liquid), so replacing the markup silently
         * kills quantity editing until the next load.
         *
         * The one case we defer it is an add made from inside the OPEN checkout
         * modal — reloading there would rip the modal away mid-interaction and
         * lose the "you've unlocked free shipping" moment. Closing the modal
         * reloads instead; the skip-to-checkout button leaves the page anyway.
         */
        if (/^\/cart(\/|$)/.test(window.location.pathname)) {
          var open = overlayOf(btn) && !overlayOf(btn).hidden;
          if (open) cartPageStale = true;
          else window.location.reload();
        }
      })
      .catch(function () {
        btn.classList.remove('is-loading');
        btn.disabled = false;
        btn.textContent = opts.errorLabel;
      });
  }

  /* ---------------------------------------------------------------------
   * Checkout intercept
   *
   * Offers the same suggestions one last time when the shopper heads for
   * checkout still short of free shipping. Bound in the CAPTURE phase at the
   * document, because the theme's drawer checkout button carries an inline
   * onclick="window.location.href='/checkout'" — a bubble-phase listener runs
   * after it and would be too late to stop the navigation.
   * ------------------------------------------------------------------- */

  var CHECKOUT_TRIGGERS = [
    '.dropdown-custom-checkout',   // cart drawer
    '#custom-checkout-btn',        // cart page
    '.custom-checkout-button',     // cart page (class form)
    'button[name="checkout"]',     // native cart form submit
    '.tt-modal-addtocart a[href^="/checkout"]' // "added to cart" modal
  ].join(',');

  function interceptShownThisSession() {
    try { return sessionStorage.getItem(INTERCEPT_SESSION_KEY) === '1'; }
    catch (e) { return false; }
  }

  function markInterceptShown() {
    try { sessionStorage.setItem(INTERCEPT_SESSION_KEY, '1'); } catch (e) {}
  }

  function openIntercept(overlay) {
    markInterceptShown();
    overlay.hidden = false;
    document.documentElement.classList.add('cart-upsell-intercept-open');
    var close = overlay.querySelector('[data-cart-upsell-intercept-close]');
    if (close && close.focus) close.focus();
  }

  document.addEventListener('click', function (event) {
    var t = event.target;
    if (!t || !t.closest) return;

    // Never block a click an app or script made on the shopper's behalf —
    // Redo drives the hidden .dropdown-checkout-original this way.
    if (!event.isTrusted) return;

    var trigger = t.closest(CHECKOUT_TRIGGERS);
    if (!trigger) return;
    if (trigger.closest('.original-checkout-wrapper')) return;
    if (interceptShownThisSession()) return;

    var overlay = document.querySelector('[data-cart-upsell-intercept]');
    if (!overlay) return;

    // Decide off what is ALREADY rendered — the refresh cycle keeps the inner
    // block current. A hidden block means there is nothing worth interrupting
    // for (gap closed, under the minimum, or no suggestion clears it), and the
    // shopper goes straight to checkout with no added latency.
    var inner = overlay.querySelector('[data-cart-upsell]');
    if (!inner || inner.hidden) return;

    event.preventDefault();
    event.stopPropagation();
    openIntercept(overlay);
  }, true);

  document.addEventListener('click', function (event) {
    var t = event.target;
    if (!t || !t.closest) return;

    var overlay = document.querySelector('[data-cart-upsell-intercept]');
    if (!overlay || overlay.hidden) return;

    if (t.closest('[data-cart-upsell-intercept-continue]')) {
      event.preventDefault();
      closeIntercept(overlay, { leaving: true });
      window.location.href = '/checkout';
      return;
    }
    if (t.closest('[data-cart-upsell-intercept-close]') ||
        t === overlay ||
        t.hasAttribute('data-cart-upsell-intercept-backdrop')) {
      event.preventDefault();
      closeIntercept(overlay);
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    var overlay = document.querySelector('[data-cart-upsell-intercept]');
    if (overlay && !overlay.hidden) closeIntercept(overlay);
  });

  /* ---------------------------------------------------------------------
   * Cart change detection
   *
   * The theme fires cart:updated from its own add-to-cart path, but apps and
   * snippets on this store (HulkApps options, Redo, the wishlist) post to
   * /cart/* directly and never announce it. Watching the requests themselves
   * catches every one of them, so the suggestions re-pick without a reload no
   * matter who changed the cart.
   *
   * ponytail: this only re-runs OUR refresh, deliberately — re-broadcasting
   * cart:updated would fan out into a dozen third-party listeners we do not
   * own for no benefit here.
   * ------------------------------------------------------------------- */

  var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  function watchCartWrites() {
    if (window.fetch) {
      var nativeFetch = window.fetch;
      window.fetch = function (input, init) {
        var url = '';
        try { url = typeof input === 'string' ? input : (input && input.url) || ''; } catch (e) {}
        var promise = nativeFetch.apply(this, arguments);
        if (CART_WRITE.test(url)) promise.then(scheduleRefresh, function () {});
        return promise;
      };
    }

    var XHR = window.XMLHttpRequest;
    if (XHR && XHR.prototype && XHR.prototype.open) {
      var nativeOpen = XHR.prototype.open;
      XHR.prototype.open = function (method, url) {
        try {
          if (CART_WRITE.test(String(url))) {
            this.addEventListener('load', scheduleRefresh);
          }
        } catch (e) {}
        return nativeOpen.apply(this, arguments);
      };
    }
  }

  watchCartWrites();

  document.addEventListener('click', function (event) {
    var btn = event.target.closest && event.target.closest('[data-cart-upsell-add]');
    if (!btn) return;
    var wrapper = btn.closest('[data-cart-upsell]');
    if (!wrapper) return;
    event.preventDefault();
    quickAdd(parseInt(btn.getAttribute('data-cart-upsell-add'), 10), btn, readOptions(wrapper));
  });

  document.addEventListener('cart:updated', scheduleRefresh);
  document.addEventListener('shopify:section:load', scheduleRefresh);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refresh);
  } else {
    refresh();
  }
})();
